import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  ReactNode,
} from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '../../store/toaster/action';
import {
  useThreatDetailQuery,
  useChangeThreatStatusMutation,
} from '../../queries/threats';
import {
  createNetworkByType,
  getHistoryStatuesApi,
  getNetworkDataByType,
  getNetworkTaskById,
  getSubDomainsApi,
  fetchGSBLiveData,
} from '../../services/singleThreatApi';
import {
  DNSDataResponseType,
  HistoryStatuesType,
  HistoryStatusesDataType,
  SingleThreatContextType,
  SingleThreatDetailsType,
  SSlDataResponseType,
  SubDomainsType,
  WhoisDataResponseType,
  GSBLiveInfoType,
} from './SingleThreatType';
import {
  apiErrorMessage,
  isRecentNetworkRecords,
  NETWORK_DATA_TYPE,
} from '../../utils';
import { changeStatusLabelType } from '../../helpers/types';

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const SingleThreatContext = createContext<SingleThreatContextType | null>(null);

interface SingleThreatProviderProps {
  children: ReactNode;
}
export const useSingleThreatContext = () => {
  const context = useContext(SingleThreatContext);
  if (!context) {
    throw new Error(
      'useThreatContext must be used within a SingleThreatProvider',
    );
  }
  return context;
};

export const SingleThreatProvider: React.FC<SingleThreatProviderProps> = ({
  children,
}) => {
  // Threat detail is owned by TanStack Query (WHEEL-339); the context only
  // holds which threat is being viewed and re-exposes the cached data under
  // the pre-existing API so consumers stay unchanged.
  const [threatParams, setThreatParams] = useState<{
    protected_id: string;
    threat_id: string;
  }>();
  const detailQuery = useThreatDetailQuery(threatParams);
  const changeStatusMutation = useChangeThreatStatusMutation();
  const singleThreatDetails = detailQuery.data;
  const [historyStatusesData, setHistoryStatusesData] = useState<
    HistoryStatusesDataType[]
  >([]);
  const [subDomainsData, setSubDomainsData] = useState<SubDomainsType[]>([]);
  const [subDomainsLoading, setSubDomainsLoading] = useState<boolean>(false);
  const [whoisData, setWhoisData] = useState<WhoisDataResponseType>();
  const [whoisDataLoading, setWhoisDataLoading] = useState<boolean>(false);
  const [sslData, setSSLData] = useState<SSlDataResponseType>();
  const [sslDataLoading, setSSLDataLoading] = useState<boolean>(false);
  const [dnsData, setDNSData] = useState<DNSDataResponseType>();
  const [dnsLoading, setDNSLoading] = useState<boolean>(false);
  const loading = !!threatParams && detailQuery.isPending;
  const [historyStatusLoading, setHistoryStatusLoading] =
    useState<boolean>(false);
  const dispatch = useDispatch();
  const [gsbLiveInfo, setGSBLiveInfo] = useState<GSBLiveInfoType | undefined>();
  const [isGSBLiveDataLoading, setIsGSBLiveDataLoading] =
    useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const fetchSingleThreatList = (params: {
    protected_id: string;
    threat_id: string;
  }) => {
    setError('');
    setThreatParams(params);
  };

  const getHistoryStatuses = async (params: HistoryStatuesType) => {
    setHistoryStatusLoading(true);
    setError('');
    try {
      const res = await getHistoryStatuesApi(params);
      setHistoryStatusesData(res.data);
    } catch (err: any) {
      console.error(err);
    } finally {
      setHistoryStatusLoading(false);
    }
  };

  const getSubDomains = async (DomainId: number) => {
    setSubDomainsLoading(true);
    setError('');
    try {
      const res = await getSubDomainsApi(DomainId);
      setSubDomainsData(res.data);
    } catch (err: any) {
      console.error(err);
    } finally {
      setSubDomainsLoading(false);
    }
  };

  const getNetworkRecords = async (
    asset_id: number,
    type:
      | NETWORK_DATA_TYPE.WHOIS
      | NETWORK_DATA_TYPE.CERTIFICATE
      | NETWORK_DATA_TYPE.RECORDS,
  ) => {
    if (type === NETWORK_DATA_TYPE.WHOIS) {
      setWhoisDataLoading(true);
    } else if (type === NETWORK_DATA_TYPE.CERTIFICATE) {
      setSSLDataLoading(true);
    } else {
      setDNSLoading(true);
    }

    setError('');
    try {
      const res = await getNetworkDataByType(asset_id, type);

      const isDataExits =
        res?.data?.items?.length > 0 &&
        isRecentNetworkRecords(type, res?.data?.items?.[0]?.created_at);

      if (isDataExits) {
        if (type === NETWORK_DATA_TYPE.WHOIS) {
          setWhoisData(res?.data);
        } else if (type === NETWORK_DATA_TYPE.CERTIFICATE) {
          setSSLData(res?.data);
        } else {
          setDNSData(res?.data);
        }
      } else {
        const resCreateNetwork = await createNetworkByType({ asset_id, type });
        // Was redux-saga's `delay` effect creator before WHEEL-339, which
        // resolves immediately outside a saga - the 2s wait never happened
        await sleep(2000);
        if (resCreateNetwork?.data?.task_id) {
          const resTaskById = await getNetworkTaskById(
            resCreateNetwork?.data?.task_id,
          );
          if (type === NETWORK_DATA_TYPE.WHOIS) {
            setWhoisData(resTaskById?.data);
          } else if (type === NETWORK_DATA_TYPE.CERTIFICATE) {
            setSSLData(resTaskById?.data);
          } else {
            setDNSData(resTaskById?.data);
          }
        }
      }
    } catch (err: any) {
      dispatch(showToast(err?.message || 'Something went wrong', 'error'));
      console.error(err);
    } finally {
      setWhoisDataLoading(false);
      setSSLDataLoading(false);
      setDNSLoading(false);
    }
  };

  const getWhoisRecords = async (params: {
    asset_id: number;
    type: string;
  }) => {
    await getNetworkRecords(params.asset_id, NETWORK_DATA_TYPE.WHOIS);
  };

  const getSSLRecords = async (params: { asset_id: number; type: string }) => {
    await getNetworkRecords(params.asset_id, NETWORK_DATA_TYPE.CERTIFICATE);
  };

  const getDnsRecords = async (params: { asset_id: number; type: string }) => {
    await getNetworkRecords(params.asset_id, NETWORK_DATA_TYPE.RECORDS);
  };

  // Returns whether the API accepted the change — the cockpit's
  // confirm-and-takedown chain only fires the takedown on true.
  const updateThreatStatus = async (
    data: changeStatusLabelType,
  ): Promise<boolean> => {
    setError('');
    try {
      // Toasts, detail-cache update, and the Redux list bridge all live in
      // the shared mutation (queries/threats.ts)
      await changeStatusMutation.mutateAsync(data);
      return true;
    } catch (err: any) {
      setError(apiErrorMessage(err));
      console.error(err);
      return false;
    }
  };

  const fetchGSBLiveInfo = async (params: string) => {
    setIsGSBLiveDataLoading(true);
    try {
      const res = await fetchGSBLiveData(params);
      setGSBLiveInfo(res?.data);
    } catch (err: any) {
      const errorMessage = apiErrorMessage(err);
      dispatch(showToast(errorMessage, 'error'));
      console.error(err);
    } finally {
      setIsGSBLiveDataLoading(false);
    }
  };

  const contextValue = useMemo(
    () => ({
      singleThreatDetails,
      loading,
      fetchSingleThreatList,
      getHistoryStatuses,
      historyStatusesData,
      historyStatusLoading,
      getSubDomains,
      subDomainsLoading,
      subDomainsData,
      getWhoisRecords,
      whoisData,
      whoisDataLoading,
      getSSLRecords,
      sslData,
      sslDataLoading,
      getDnsRecords,
      dnsData,
      dnsLoading,
      updateThreatStatus,
      statusUpdating: changeStatusMutation.isPending,
      fetchGSBLiveInfo,
      gsbLiveInfo,
      isGSBLiveDataLoading,
    }),
    [
      singleThreatDetails,
      loading,
      error,
      historyStatusesData,
      historyStatusLoading,
      subDomainsData,
      subDomainsLoading,
      whoisData,
      whoisDataLoading,
      sslData,
      sslDataLoading,
      dnsData,
      dnsLoading,
      changeStatusMutation.isPending,
      gsbLiveInfo,
      isGSBLiveDataLoading,
    ],
  );

  return (
    <SingleThreatContext.Provider value={contextValue}>
      {children}
    </SingleThreatContext.Provider>
  );
};
